import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { createContext, use } from 'react'
import { LoaderFunction, redirect } from 'react-router'

import type { AuthUser } from '@rdm/common'

export type AuthState = {
  token: string
  sessionExpiry: string
  user: AuthUser
}

export const AuthContext = createContext<AuthState | null>(null)

export const useAuth = () => {
  return use(AuthContext)
}

export const useRequiredAuth = (): AuthState => {
  const authState = use(AuthContext)
  if (authState === null) {
    throw new Error(
      'useRequiredAuth called without an active session. Ensure the route is protected by requireAuthLoader so this state is unreachable.',
    )
  }
  return authState
}

export const getAuthStateFromSession = (): AuthState | null => {
  const authState = sessionStorage.getItem('authState')
  if (!authState) return null

  try {
    const parsed = JSON.parse(authState) as AuthState
    if (!parsed || !parsed.token || !parsed.user || !parsed.sessionExpiry) return null

    if (dayjs(parsed.sessionExpiry).isBefore(dayjs())) {
      // Session expired
      sessionStorage.removeItem('authState')
      return null
    }

    return parsed
  } catch {
    // Handle corrupted JSON gracefully
    sessionStorage.removeItem('authState')
    return null
  }
}

export const setAuthStateInSession = (authState: AuthState) => {
  sessionStorage.setItem('authState', JSON.stringify(authState))
}

export const refreshAuthUserInSession = (user: AuthUser) => {
  const authState = getAuthStateFromSession()
  if (!authState) return
  authState.user = user
  setAuthStateInSession(authState)
}

// Module-level config so route loaders can call attemptAuth without threading config through.
let authConfig: { apiUrl: string; cookieName: string } | null = null

// In-flight /auth request, shared across concurrent callers to avoid duplicate fetches and races
// between parent (Root) and child (requireAuthLoader) loaders that run in parallel.
let inFlightAuth: Promise<AuthState | null> | null = null

// Why the last /auth attempt was rejected. A UQ user with a valid SSO cookie can still be
// rejected by the API (not yet synced from the CIP, or inactivated); redirecting them to /login
// would bounce straight back through SSO in an endless loop, so the route loaders use this to
// send them to a dedicated page instead.
type AuthFailureReason = 'UNKNOWN_USER' | 'INACTIVE_USER'
let lastAuthFailure: AuthFailureReason | null = null

const parseAuthFailure = async (response: Response): Promise<AuthFailureReason | null> => {
  try {
    const body = (await response.json()) as { message?: string }
    if (body?.message === 'UNKNOWN_USER' || body?.message === 'INACTIVE_USER') return body.message
    return null
  } catch {
    // Non-JSON body (e.g. a plain 401) - not a known rejection
    return null
  }
}

export const configureAuth = (config: { apiUrl: string; cookieName: string }) => {
  authConfig = config
}

/**
 * Attempts to authenticate the user by checking sessionStorage and, if not found,
 * by making a request to the server if a refresh cookie is present.
 *
 * Concurrent callers share a single in-flight /auth request so that route loaders
 * running in parallel don't race each other.
 *
 * @returns The current auth state, or null if not authenticated
 */
export const attemptAuth = async (apiUrl: string, cookieName: string): Promise<AuthState | null> => {
  const authState = getAuthStateFromSession()
  if (authState) return authState

  // If we don't have an authState in sessionStorage, attempt to fetch it from the server
  const refreshCookie = Cookies.get(cookieName)
  if (!refreshCookie) return null

  if (inFlightAuth) return inFlightAuth

  inFlightAuth = (async () => {
    try {
      const r = await fetch(`${apiUrl}/auth`, { credentials: 'include' })

      if (!r.ok) {
        // Record known rejections (UNKNOWN_USER / INACTIVE_USER) so the route loaders can
        // break the SSO redirect loop with a dedicated page instead of bouncing to /login
        lastAuthFailure = await parseAuthFailure(r)
        return null
      }

      lastAuthFailure = null
      const authResult = await r.json()
      if (authResult?.user && dayjs(authResult.sessionExpiry).isAfter(dayjs())) {
        setAuthStateInSession(authResult)
        return authResult
      } else {
        return null
      }
    } catch {
      return null
    } finally {
      inFlightAuth = null
    }
  })()

  return inFlightAuth
}

export const destroyAuthSession = () => {
  sessionStorage.removeItem('authState')
}

export const getSessionExpiry = () => {
  const authState = getAuthStateFromSession()
  return authState ? dayjs(authState.sessionExpiry) : null
}

// --- React Router Loaders ---

// Helper to encode the FULL return path (including domain)
const getReturnPath = (requestUrl: string) => {
  return window.btoa(requestUrl)
}

// Build the redirect for an unauthenticated user on a protected route. Known auth rejections
// get their dedicated page (redirecting to /login would loop forever through SSO); everything
// else goes to /login as usual.
const unauthenticatedRedirect = (requestUrl: string): Response => {
  if (lastAuthFailure === 'UNKNOWN_USER') return redirect('/unknown-user')
  if (lastAuthFailure === 'INACTIVE_USER') return redirect('/inactive-user')

  let loginUrl = '/login?return=' + getReturnPath(requestUrl)

  // Carry the provider hint from an email deep link so the login page can route the user straight
  // to their auth provider instead of the chooser.
  const authMethod = new URL(requestUrl).searchParams.get('authMethod')
  if (authMethod) loginUrl += '&authMethod=' + encodeURIComponent(authMethod)

  return redirect(loginUrl)
}

// Read sessionStorage; if empty, fall back to attemptAuth so that a refresh cookie
// from a fresh SSO redirect can seed the session. Prevents a race where this loader
// runs in parallel with Root's loader on first page load after SSO.
const resolveAuthForLoader = async (): Promise<AuthState | null> => {
  const authState = getAuthStateFromSession()
  if (authState) return authState
  if (!authConfig) return null
  return attemptAuth(authConfig.apiUrl, authConfig.cookieName)
}

export function requireAuthLoader(requiredRole?: string, onlyUq?: boolean): LoaderFunction {
  // This is the actual loader function that will be executed by React Router
  return async ({ request }) => {
    const authState = await resolveAuthForLoader()

    // 1. Check if the user is logged in
    if (!authState || !authState.user) {
      // If not, redirect them to the login page (or a dedicated rejection page).
      throw unauthenticatedRedirect(request.url)
    }

    // 2. If a role is required, check if the user has that role
    if (requiredRole && !authState.user.roles.includes(requiredRole)) {
      // If they don't have the role, redirect to the unauthorized page.
      throw redirect('/unauthorized')
    }

    // 3. If onlyUq is true, check if the user is a UQ user
    if (onlyUq && !authState.user.roles.includes('uq')) {
      if (authState.user.roles.includes('user-thesis-examiner')) {
        throw redirect('/thesis/dashboard')
      }

      throw redirect('/external-landing')
    }

    // 4. If everything is fine, allow access to the route by returning null
    return authState
  }
}

export function requireAnyRoleLoader(roles: string[]): LoaderFunction {
  // This is the actual loader function that will be executed by React Router
  return async ({ request }) => {
    const authState = await resolveAuthForLoader()

    // 1. Check if the user is logged in
    if (!authState || !authState.user) {
      // If not, redirect them to the login page (or a dedicated rejection page).
      throw unauthenticatedRedirect(request.url)
    }

    // 2. If a role is required, check if the user has that role
    const hasRole = roles.some((role) => authState.user.roles.includes(role))
    if (!hasRole) {
      // If they don't have the role, redirect to the unauthorized page.
      throw redirect('/unauthorized')
    }

    // 3. If everything is fine, allow access to the route by returning null
    return authState
  }
}
